/**
 * tableRange.ts
 *
 * Utilities for resolving relative table range coordinates to absolute indices,
 * and applying ranges to extract sub-tables from grid data.
 */

import { RelativeCoord, TableRange } from '../types/gotypes'
import type { PlatinumData } from '../types/carbon/models/PlatinumData'
import { calculateBandsForVec } from '../components/platinum-table-new/layout'

/**
 * Resolves a relative coordinate to an absolute index given the current dimension size.
 *
 * - anchor:'start', offset:2, totalSize:10 → 2
 * - anchor:'end', offset:0, totalSize:10 → 9
 * - anchor:'end', offset:2, totalSize:10 → 7
 *
 * Result is clamped to [0, totalSize - 1].
 */
export function resolveCoord(coord: RelativeCoord, totalSize: number): number {
  if (totalSize <= 0) return 0

  let index: number
  if (coord.anchor === 'end') {
    index = totalSize - 1 - coord.offset
  } else {
    // 'start'
    index = coord.offset
  }

  return Math.max(0, Math.min(index, totalSize - 1))
}

/**
 * Converts an absolute index to a relative coordinate based on proximity to boundaries.
 * If closer to start (or equidistant), uses 'start' anchor.
 * If closer to end, uses 'end' anchor.
 */
export function toRelativeCoord(index: number, totalSize: number): RelativeCoord {
  if (totalSize <= 0) return { anchor: 'start', offset: 0 }

  const distFromStart = index
  const distFromEnd = totalSize - 1 - index

  if (distFromEnd < distFromStart) {
    return { anchor: 'end', offset: distFromEnd }
  }
  return { anchor: 'start', offset: distFromStart }
}

/**
 * Resolves a full TableRange to absolute row/col indices (inclusive).
 * Returns { rowStart, rowEnd, colStart, colEnd } as absolute 0-based indices
 * within the data portion of the table (excluding headers).
 */
export function resolveTableRange(
  range: TableRange,
  totalDataRows: number,
  totalDataCols: number,
): {
  rowStart: number
  rowEnd: number
  colStart: number
  colEnd: number
} {
  const rowStart = resolveCoord(range.row_start, totalDataRows)
  const rowEnd = resolveCoord(range.row_end, totalDataRows)
  const colStart = resolveCoord(range.col_start, totalDataCols)
  const colEnd = resolveCoord(range.col_end, totalDataCols)

  return {
    rowStart: Math.min(rowStart, rowEnd),
    rowEnd: Math.max(rowStart, rowEnd),
    colStart: Math.min(colStart, colEnd),
    colEnd: Math.max(colStart, colEnd),
  }
}

/**
 * Converts two absolute indices (start and end of a selection along one axis)
 * into a pair of RelativeCoords.
 */
export function toRelativeRange(
  startIndex: number,
  endIndex: number,
  totalSize: number,
): { start: RelativeCoord; end: RelativeCoord } {
  const lo = Math.min(startIndex, endIndex)
  const hi = Math.max(startIndex, endIndex)

  return {
    start: toRelativeCoord(lo, totalSize),
    end: toRelativeCoord(hi, totalSize),
  }
}

/**
 * Applies a TableRange to PlatinumData, returning a new PlatinumData
 * containing only the selected rows and columns.
 *
 * The range coordinates refer to data-cell indices (excluding headers).
 * Side vecs map to data rows via band counts; top vecs map 1:1 to columns.
 * Groups are cleared since they can't be accurately sliced.
 */
export function applyRangeToPlatinumData(
  data: PlatinumData,
  range: TableRange,
): PlatinumData {
  const sideVecs = data.side?.vecs || []
  const topVecs = data.top?.vecs || []
  const cellRows = data.cells?.rows || []

  // Build a mapping from data-row index to side-vec index
  // Each vec can produce multiple rows (bands)
  const vecBandCounts = sideVecs.map(v => calculateBandsForVec(v, data.properties))
  const totalDataRows = vecBandCounts.reduce((sum, n) => sum + n, 0)
  const totalDataCols = topVecs.length

  if (totalDataRows === 0 || totalDataCols === 0) return data

  const resolved = resolveTableRange(range, totalDataRows, totalDataCols)

  // Determine which vecs (and their band rows) fall within the row range
  let rowIdx = 0
  let sliceRowStart = 0
  let sliceRowEnd = 0
  let vecStart = -1
  let vecEnd = -1

  for (let vi = 0; vi < sideVecs.length; vi++) {
    const bands = vecBandCounts[vi]
    const vecRowStart = rowIdx
    const vecRowEnd = rowIdx + bands - 1

    // Check if this vec overlaps with the resolved row range
    if (vecRowEnd >= resolved.rowStart && vecRowStart <= resolved.rowEnd) {
      if (vecStart === -1) {
        vecStart = vi
        sliceRowStart = vecRowStart
      }
      vecEnd = vi
      sliceRowEnd = vecRowEnd
    }

    rowIdx += bands
  }

  if (vecStart === -1) return data

  // Slice the data
  const newSideVecs = sideVecs.slice(vecStart, vecEnd + 1)
  const newTopVecs = topVecs.slice(resolved.colStart, resolved.colEnd + 1)

  // Slice cell rows: from the first band-row of vecStart to the last band-row of vecEnd,
  // clamped to the resolved range
  const cellRowStart = Math.max(sliceRowStart, resolved.rowStart)
  const cellRowEnd = Math.min(sliceRowEnd, resolved.rowEnd)
  const newCellRows = cellRows.slice(cellRowStart, cellRowEnd + 1).map(row => ({
    ...row,
    cell: (row.cell || []).slice(resolved.colStart, resolved.colEnd + 1),
  }))

  return {
    ...data,
    top: {
      ...data.top,
      groups: [], // Groups can't be accurately sliced
      vecs: newTopVecs,
    },
    side: {
      ...data.side,
      groups: [], // Groups can't be accurately sliced
      vecs: newSideVecs,
    },
    cells: {
      ...data.cells,
      rows: newCellRows,
    },
  }
}
